import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { ReactComponent as SendIcon } from './icons/send_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
import { ReactComponent as MicIcon } from './icons/mic_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
import { ReactComponent as MicOffIcon } from './icons/mic_off_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
import Button from '@mui/material/Button';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import rehypeRaw from 'rehype-raw';
import { ReactComponent as HeadsetMicIcon } from './icons/headset_mic_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
import { ReactComponent as HelpOutlineIcon } from './icons/info_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
import { ThreeDots } from 'react-loader-spinner';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import { ReactComponent as VoiceOffIcon } from './icons/voice_over_off_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
import { ReactComponent as VoiceOnIcon } from './icons/record_voice_over_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
import ProductMediaSidebar from './ProductMediaSidebar';
import { useCallback, useMemo } from 'react';


const theme = createTheme({
  palette: {
  primary: {
  main: '#1f3243', // Colore primario (esempio)
  },
  secondary: {
  main: '#d0df00', // Colore secondario per gli stati attivi
  },
  },
  typography: {
  fontFamily: 'Montserrat, sans-serif',
  },
  components: {
  MuiTypography: {
  styleOverrides: {
  root: {
  fontFamily: 'Montserrat, sans-serif',
  },
  },
  },
  MuiTextField: {
  styleOverrides: {
  root: {
  fontFamily: 'Montserrat, sans-serif',
  },
  },
  },
  },
  });
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

  function ChatMessage({ message }) {
    return (
    <Typography component="div" sx={{ fontFamily: 'Montserrat, sans-serif' }}>
    <ReactMarkdown rehypePlugins={[rehypeRaw]}>{message}</ReactMarkdown>
    </Typography>
    );
    }
    
    const UserForm = ({ onSubmit }) => {
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [contactReason, setContactReason] = useState('');
    
    const handleSubmit = (e) => {
    e.preventDefault();

    onSubmit({
      fullName,
      email,
      phone,
      contactReason
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Typography variant="h6">Inserisci i tuoi dati</Typography>
      <TextField
        label="Nome e Cognome"
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
        required
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Telefono"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        required
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Motivo del Contatto"
        value={contactReason}
        onChange={(e) => setContactReason(e.target.value)}
        required
        fullWidth
        multiline
        rows={4}
        sx={{ mb: 2 }}
      />
      <Button type="submit" variant="contained" fullWidth>
        Invia
      </Button>
    </Box>
  );
};

const ChatBot = () => {
  const [messages, setMessages] = useState(() => {
  const savedMessages = sessionStorage.getItem('chatMessages');
  return savedMessages ? JSON.parse(savedMessages) : [];
  });
  const [isTtsActive, setIsTtsActive] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState(null); 

  const [productData, setProductData] = useState([]);
  const [isProductDataLoaded, setIsProductDataLoaded] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [isVoiceAssistantActive, setIsVoiceAssistantActive] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const recentLinks = useRef([]);
  const endOfMessagesRef = useRef(null);
  const videoRef = useRef(null);
  const recognitionRef = useRef(null);
  const silenceTimeoutRef = useRef(null);
  const isVoiceAssistantActiveRef = useRef(isVoiceAssistantActive);
  const isProcessingResponseRef = useRef(false);
  const audioRef = useRef(null);
  const helpOverlayRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [productMedia, setProductMedia] = useState(null);
  const toggleSidebar = () => {
  setIsSidebarOpen(!isSidebarOpen);
  };
  
  useEffect(() => {
  isVoiceAssistantActiveRef.current = isVoiceAssistantActive;
  }, [isVoiceAssistantActive]);

  useEffect(() => {
    const initializeChat = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/get-product-data`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        setProductData(data);
        setIsProductDataLoaded(true);

        const initialMessage = {
          sender: 'bot',
          text: "Vuoi attivare l'assistenza avanzata?",
          options: [
            { text: 'Sì', action: 'activateAdvancedAssistance' },
            { text: 'No', action: 'startNormalConversation' },
          ],
        };
        setMessages([initialMessage]);
      } catch (error) {
        console.error('Errore nel caricamento dei dati dei prodotti:', error);
        setMessages([
          {
            sender: 'bot',
            text: "C'è stato un problema nel caricamento dei dati dei prodotti. Riprova più tardi.",
          },
        ]);
      }
    };

    initializeChat();
  }, []);

  useEffect(() => {
    const fetchWelcomeMessage = async () => {
      try {
        const response = await fetch("/info.json");
        if (!response.ok) {
          throw new Error(`Errore nel caricamento di info.json: ${response.status}`);
        }
        const data = await response.json();
        if (data.welcomeMessage) {
          setWelcomeMessage(data.welcomeMessage);
        } else {
          setWelcomeMessage("Benvenuto/a nel mondo Hygenia. Come posso aiutarti?"); // Fallback
        }
      } catch (error) {
        console.error("Errore nel caricamento del messaggio di benvenuto:", error);
        setWelcomeMessage("Benvenuto/a nel mondo Hygenia. Come posso aiutarti?"); // Fallback se c'è un errore
      }
    };
  
    fetchWelcomeMessage();
  }, []);
  
  useEffect(() => {
    if (endOfMessagesRef.current) {
      endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    const handleLinkClick = (event) => {
      if (event.target.tagName === 'A') {
        event.preventDefault();
        const url = event.target.href;
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    };
  
    document.addEventListener('click', handleLinkClick);
  
    return () => {
      document.removeEventListener('click', handleLinkClick);
    };
  }, []);

  useEffect(() => {
    if (!isTtsActive && audioRef.current) {
      // Se la TTS è stata spenta e c'è un audio in riproduzione, fermalo
      audioRef.current.pause();
      audioRef.current.currentTime = 0; // opzione per riportarlo all'inizio
      audioRef.current = null;
    }
  }, [isTtsActive]);  

  const handleHelpClick = () => {
    setShowHelp((prevShowHelp) => !prevShowHelp);
  };  

  const HelpOverlay = ({ onClose }) => (
    <Box
      sx={{
        position: 'absolute', // Da absolute perché il genitore ha position: relative
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        zIndex: 2, // Deve essere maggiore del Paper (che abbiamo zIndex: 1)
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 2,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Typography variant="h6" component="h2" sx={{ mb: 2, textAlign: 'center' }}>
        Guida all'uso del Chatbot
      </Typography>
    
      <Typography
        variant="body1"
        sx={{
          fontSize: '1rem',
          textAlign: 'center',
          px: 2,
          maxWidth: '90%',
          wordWrap: 'break-word',
        }}
      >
        Benvenuto nel nostro chatbot! Ecco alcuni suggerimenti su come utilizzarlo:
      </Typography>
      <ul
        style={{
          paddingLeft: '1.5em',
          marginTop: '1em',
          fontSize: '1rem',
          textAlign: 'left',
          maxWidth: '90%',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <li>Puoi chiedere informazioni sui nostri prodotti e servizi.</li>
        <li>Clicca sull'icona del <b>Microfono</b> per attivare l'assistente vocale.</li>
        <li>Scrivi un messaggio nella barra in basso per fare domande.</li>
        <li>Clicca sull'icona <b>Mail</b> per fissare un appuntamento.</li>
      </ul>
    
      <Button variant="contained" color="secondary" onClick={onClose} sx={{ mt: 3 }}>
        Chiudi
      </Button>
    </Box>
  );
  
  const initializeRecognition = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Il tuo browser non supporta il riconoscimento vocale.');
      return;
    }

    const recognitionInstance = new SpeechRecognition();
    recognitionInstance.lang = 'it-IT';
    recognitionInstance.continuous = false;
    recognitionInstance.interimResults = false;

    recognitionInstance.onresult = async (event) => {
      const transcript = event.results[0][0].transcript;
      handleVoiceInput(transcript);
    };

    recognitionInstance.onerror = (event) => {
      console.error('Errore nel riconoscimento vocale:', event.error);
    };

    recognitionInstance.onend = () => {
      clearTimeout(silenceTimeoutRef.current);
      if (isVoiceAssistantActiveRef.current && !isProcessingResponseRef.current) {
        try {
          recognitionInstance.start();
        } catch (error) {
          console.error('Errore nel riavvio del riconoscimento vocale:', error);
        }
      }
    };

    recognitionRef.current = recognitionInstance;
  };

  const startVoiceRecognition = () => {
    if (!recognitionRef.current) {
      initializeRecognition();
    }

    setIsVoiceAssistantActive(true);
    isVoiceAssistantActiveRef.current = true;

    try {
      recognitionRef.current.start();
    } catch (error) {
      console.error("Errore nell'avvio del riconoscimento vocale:", error);
    }
  };

  const stopVoiceRecognition = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsVoiceAssistantActive(false);
    isVoiceAssistantActiveRef.current = false;
  };

  const toggleVoiceRecognition = () => {
    if (isVoiceAssistantActive) {
      stopVoiceRecognition();
    } else {
      // Sto per attivare il microfono, spengo la TTS
      if (isTtsActive) setIsTtsActive(false);
      startVoiceRecognition();
    }
  };
  
  const handleVoiceInput = async (transcript) => {
    let correctedTranscript = transcript
      .replace(/\bigenia\b/gi, 'Hygenia') // Pronuncia più comune in italiano
      .replace(/\biggenia\b/gi, 'Hygenia') // Possibile errore di trascrizione
      .replace(/\bigen\b/gi, 'Hygenia') // Abbreviazione o trascrizione errata
      .replace(/\bhyjenia\b/gi, 'Hygenia') // Trascrizione fonetica alternativa
      .replace(/\bigenya\b/gi, 'Hygenia') // Varianti di trascrizione
      .replace(/\bhygen\b/gi, 'Hygenia'); // Abbreviazione o errore fonetico
  
    const isOpenLinkCommand = openLinkByVoiceCommand(correctedTranscript);
  
    isProcessingResponseRef.current = true;
    recognitionRef.current.stop();
  
    if (!isOpenLinkCommand) {
      const userMessage = { text: correctedTranscript, sender: 'user' };
      setMessages((prevMessages) => [...prevMessages, userMessage]);
  
      if (!showUserForm) {
        // Ottieni direttamente la risposta del bot
        const botResponse = await processUserMessage(correctedTranscript);
  
        // Spegni il microfono se attivo
        if (isVoiceAssistantActive) {
          stopVoiceRecognition();
        }
  
        // Attiva il TTS e leggila direttamente
        setIsTtsActive(true);
        playMessageAsAudio(botResponse);
      }
    }
  
    isProcessingResponseRef.current = false;
  };
         
  const handleUserInput = async () => {
    if (!userInput.trim()) return;
  
    // Salva il messaggio dell'utente prima di svuotare il campo
    const userMessage = { text: userInput, sender: 'user' };
  
    // Svuota il campo di input subito
    setUserInput('');
  
    // Aggiungi il messaggio dell'utente alla lista dei messaggi
    setMessages((prevMessages) => [...prevMessages, userMessage]);
  
    // Controlla se l'utente vuole fissare un appuntamento
    if (userInput.toLowerCase().includes('prendiamo un appuntamento')) {
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: 'bot', text: 'Vuoi fissare un appuntamento?', options: [{ text: 'Conferma', action: 'confirmAppointment' }, { text: 'Annulla', action: 'cancelAppointment' }] },
      ]);
    } else {
      // Invoca la funzione per processare il messaggio
      await processUserMessage(userInput);
    }
  };
  
  const enrichProductLink = (responseText) => {
    let enrichedText = responseText;
    recentLinks.current = []; // Resetta la lista dei link
  
    // Regex per trovare i link in Markdown
    const linkRegex = /\[([^\]]+)\]\((http[^\)]+)\)/g;
  
    let matches = [];
    let match;
  
    // Trova tutti i link nella risposta
    while ((match = linkRegex.exec(responseText)) !== null) {
      matches.push({
        fullMatch: match[0],
        linkText: match[1],
        url: match[2],
      });
    }
  
    // Processa i link trovati
    matches.forEach((match, index) => {
      const linkNumber = index + 1;
      recentLinks.current.push(match.url); // Memorizza l'URL
  
      // Crea un link numerato
      const numberedLink = `[${match.linkText} [Link ${linkNumber}]](${match.url})`;
  
      // Sostituisci il link originale con quello numerato
      enrichedText = enrichedText.replace(match.fullMatch, numberedLink);
    });
  
    return enrichedText;
  };
                      
  const processUserMessage = async (messageText) => {
    setIsLoading(true);
    const aiResponse = await getChatGPTResponse(messageText);
    // Se la risposta è strutturata, popola recentLinks.current con i link dei prodotti
  if (aiResponse.type === "structured" && aiResponse.products && aiResponse.products.length > 0) {
    recentLinks.current = aiResponse.products.map(prod => prod["link puro"]);
  } else {
    recentLinks.current = []; // Altrimenti svuotala
  }
  
  let botMessage;
  if (aiResponse.type === "structured") {
    // Memorizza direttamente l'oggetto strutturato
    botMessage = { ...aiResponse, sender: 'bot' };
  } else {
    // Se plain, crea il messaggio con il testo semplice
    botMessage = { text: aiResponse.text, sender: 'bot' };
  }
    setMessages((prevMessages) => [...prevMessages, botMessage]);
    setIsLoading(false);
    return aiResponse;
  };
  
  const pronunciationMap = {
    'HyBiz': 'Hai-Biz',
    'XWEB': 'Ix-Web',
    'XCOMMERCE': 'Ix-Commerce',
    'XASSISTANT': 'Ix-Assistant',
    'Innovative Solution': 'Innovativ Soluscion',
    'AR': 'E-Ar',
    'VR': 'Vi-Ar',
    'EXR': 'I-Ex-Ar',
    'Solution': 'Soluscion'
};

const adjustPronunciation = (text) => {
    Object.keys(pronunciationMap).forEach((key) => {
        const regex = new RegExp(`\\b${key}\\b`, 'gi');
        text = text.replace(regex, pronunciationMap[key]);
    });
    return text;
};

const playMessageAsAudio = async (originalMessage) => {
  try {
    console.log('Testo originale per la TTS:', originalMessage);
    let ttsText = '';

    if (typeof originalMessage === 'string') {
      ttsText = originalMessage;
    } else if (typeof originalMessage === 'object' && originalMessage.text) {
      // Aggiunge il testo principale
      ttsText += originalMessage.text + ' ';
      
      // Aggiunge, per ciascun prodotto, nome, descrizione e caratteristiche tecniche
      if (originalMessage.products && originalMessage.products.length > 0) {
        originalMessage.products.forEach(product => {
          if (product.name) {
            ttsText += product.name + '. ';
          }
          if (product.description) {
            ttsText += product.description + '. ';
          }
          if (product["caratteristiche tecniche"]) {
            ttsText += product["caratteristiche tecniche"] + '. ';
          }
        });
      }
      
      // Aggiunge il prompt finale
      if (originalMessage.prompt) {
        ttsText += originalMessage.prompt;
      }
    } else {
      ttsText = '';
    }

    // Applica la correzione della pronuncia
    ttsText = adjustPronunciation(ttsText);
    console.log('Testo per la TTS dopo adjustPronunciation:', ttsText);

    // Pulisce il testo
    ttsText = ttsText
      .replace(/\*\*(.*?)\*\*/g, '$1') // Rimuove eventuali marcatori di grassetto
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1') // Rimuove il markdown dei link
      .replace(/\[Link\s+\d+\]/g, '') // Rimuove il testo dei link numerati
      .replace(/https?:\/\/\S+/g, '') // Rimuove URL rimanenti
      .replace(/\*/g, '')
      .replace(/[()]/g, '')
      .trim()
      .replace(/\s+/g, ' ');

    console.log('Testo dopo la pulizia:', ttsText);

    // Richiesta al backend per la sintesi vocale
    const response = await fetch(`${BACKEND_URL}/tts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: ttsText })
    });
  
    if (!response.ok) {
      throw new Error('Errore durante la sintesi vocale');
    }
  
    const data = await response.json();
    const audioBlob = base64ToBlob(data.audioContent, 'audio/mp3');
    const audioUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioUrl);
    audioRef.current = audio;
  
    audio.onended = () => {
      setIsTtsActive(false);
      // Riattiva il microfono solo se l'utente non l'ha disattivato
      if (isVoiceAssistantActiveRef.current) {
        startVoiceRecognition();
      }
    };
  
    audio.play();
  
  } catch (error) {
    console.error('Errore TTS:', error);
  }
};

  // Utility per convertire base64 in Blob
  function base64ToBlob(base64, type = 'application/octet-stream') {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type });
  }  
        
  // Funzione per aprire un link specifico in base al comando vocale
  const openLinkByVoiceCommand = (transcript) => {
    const match = transcript.match(/apri link (?:numero )?(\d+)/i);
    if (match) {
      const linkNumber = parseInt(match[1], 10);
      if (linkNumber > 0 && linkNumber <= recentLinks.current.length) {
        const url = recentLinks.current[linkNumber - 1];
        window.open(url, '_blank', 'noopener,noreferrer');
        console.log(`Aperto link ${linkNumber}: ${url}`);
  
        // Disabilita il riconoscimento vocale quando si apre un link
        stopVoiceRecognition();
  
        return true; // Indica che è stato riconosciuto ed eseguito un comando per aprire un link
      } else {
        console.log(`Link numero ${linkNumber} non trovato.`);
        return false;
      }
    }
    return false; // Nessun comando per aprire un link riconosciuto
  };
                   
  const handleUserFormSubmit = async (formData) => {
    const userData = {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        contactReason: formData.contactReason
    };

    try {
      const response = await fetch(`${BACKEND_URL}/send-appointment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userData }), // Passa i dati senza appointmentDateTime
      });

      const data = await response.json();

      if (response.ok) {
        setMessages((prevMessages) => [
          ...prevMessages,
          { sender: 'bot', text: 'La tua richiesta è stata inviata con successo e verrai presto ricontattato!' },
        ]);
        setShowUserForm(false);
      } else {
        setMessages((prevMessages) => [
          ...prevMessages,
          { sender: 'bot', text: data.error || 'Si è verificato un errore durante l\'invio del modulo, riprova più tardi.' },
        ]);
      }
    } catch (error) {
      console.error('Errore:', error);
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: 'bot', text: 'C\'è stato un problema nella comunicazione con il server. Riprova più tardi.' },
      ]);
    }
  };
  
  const handleOptionSelected = (action) => {
    if (action === 'confirmAppointment') {
      setShowUserForm(true);  // Mostra il form per l'appuntamento
    } else if (action === 'cancelAppointment') {
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: 'bot', text: 'Appuntamento annullato, continuiamo la chat.' }
      ]);
    } else if (action === 'activateAdvancedAssistance') {
      // Rimuovi il messaggio iniziale e avvia il video
      setMessages((prevMessages) => prevMessages.slice(0, -1));
      playIntroVideo();
    } else if (action === 'startNormalConversation') {
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: 'bot', text: welcomeMessage || "Caricamento messaggio di benvenuto..." }
      ]);
    }    
  };
  
  const playIntroVideo = () => {
    const videoMessage = {
      sender: 'bot',
      type: 'video',
      content: process.env.PUBLIC_URL + '/media/videos/Intro.mp4',
    };
    setMessages((prevMessages) => [...prevMessages, videoMessage]);
  };

  const getChatGPTResponse = async (message) => {
    try {
      const response = await fetch(`${BACKEND_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, userId: 'user123' }),
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const data = await response.json();
      console.log("Risposta da OpenAI (dal backend):", data.reply);
  
      // Proviamo a fare il parsing della risposta come JSON
      try {
        const parsed = JSON.parse(data.reply);
        // Se parsed ha i campi attesi, aggiungiamo un campo "type"
        if (parsed && typeof parsed === 'object' && parsed.text) {
          parsed.type = "structured";
          return parsed;
        }
      } catch (e) {
        // Se il parsing fallisce, significa che il JSON è malformato o incompleto.
        console.error("Errore nel parsing del JSON dalla risposta dell'AI:", e);
        // Restituiamo un messaggio di errore specifico invece del JSON rotto.
        return { type: "plain", text: "Mi dispiace, ho avuto un problema nel formattare la risposta. Potresti provare a riformulare la tua domanda in modo più specifico?" };
      }
  
      // Fallback se il JSON è valido ma non ha la struttura attesa
      return { type: "plain", text: data.reply };
    } catch (error) {
      console.error('Error in getChatGPTResponse:', error);
      return { type: "plain", text: "Mi dispiace, c'è stato un errore nella comunicazione con il server." };
    }
  };
  
 // Funzione helper per escapare i caratteri speciali in una stringa
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const handleProductRecognition = useCallback((message) => {
  const text = message.text;
  let lastAcceptedEnd = 0;
  let segments = [];
  let matches = [];

  // Ordina i prodotti per lunghezza decrescente (i più lunghi prima)
  const sortedProductData = [...productData].sort(
    (a, b) => b.nomeProdotto.trim().length - a.nomeProdotto.trim().length
  );

  console.log("Processing message text:", text);

  // Per ogni prodotto, crea una regexp che:
  // • Inizi con un confine di parola (\b)
  // • Usi il nome escapato
  // • Richieda (lookahead) che dopo il nome ci sia uno spazio o la fine della stringa
  // I flag: g (global), i (case-insensitive), u (Unicode)
  sortedProductData.forEach((product) => {
    const productName = product.nomeProdotto.trim();
    const regex = new RegExp('\\b' + escapeRegExp(productName) + '(?=\\s|$)', 'giu');
    let match;
    while ((match = regex.exec(text)) !== null) {
      matches.push({
        start: match.index,
        end: regex.lastIndex,
        product,
        matchText: match[0]
      });
      console.log(`Found match for product "${productName}": "${match[0]}" at index ${match.index}`);
    }
  });

  // Ordina i match per indice di inizio
  matches.sort((a, b) => a.start - b.start);

  // Per evitare duplicazioni, teniamo traccia dei prodotti già matchati
  const matchedProducts = new Set();

  // Itera sui match per costruire i segmenti
  matches.forEach((m) => {
    // Se il match inizia dopo la fine dell'ultimo segmento accettato, aggiungi il testo intermedio
    if (m.start > lastAcceptedEnd) {
      const textSegment = text.slice(lastAcceptedEnd, m.start);
      segments.push({ type: 'text', text: textSegment });
      console.log("Created text segment:", textSegment);
    }
    // Se questo prodotto non è già stato matchato, aggiungilo come segmento
    const productKey = m.product.nomeProdotto.trim().toLowerCase();
    if (!matchedProducts.has(productKey)) {
      segments.push({ type: 'product', text: m.matchText, product: m.product });
      matchedProducts.add(productKey);
      console.log("Created product segment:", m.matchText, "for product:", m.product.nomeProdotto);
      lastAcceptedEnd = m.end;
    } else {
      console.log("Skipping duplicate product:", m.product.nomeProdotto);
    }
  });

  // Se c'è ancora del testo dopo l'ultimo match, aggiungilo
  if (lastAcceptedEnd < text.length) {
    const textSegment = text.slice(lastAcceptedEnd);
    segments.push({ type: 'text', text: textSegment });
    console.log("Created final text segment:", textSegment);
  }

  if (segments.length === 0) {
    segments.push({ type: 'text', text });
    console.log("No segments created, using full text");
  }

  console.log("Final segments:", segments);
  return segments;
}, [productData]);

  const handleViewMedia = (product) => {
    console.log("Prodotto selezionato:", product);
    
    // Costruiamo un oggetto media estraendo le proprietà direttamente dal prodotto
    const media = {
      url3Dviewer: product.url3Dviewer,
      urlImage: product.urlImage,
      urlVideo: product.urlVideo,
    };
  
    console.log("Media trovati:", media);
    
    setProductMedia(media);
    setIsSidebarOpen(true);
  };
  
  // Definizione della funzione helper per il rendering dei segmenti
  const renderStructuredMessage = (message) => {
    // Visualizza il testo principale
    const parts = [];
    parts.push(
      <Typography key="text" style={{ marginBottom: '8px', whiteSpace: 'pre-line' }}>
        {message.text}
      </Typography>
    );
    
    // Se ci sono prodotti, itera e visualizzali
    if (message.products && message.products.length > 0) {
      message.products.forEach((prod, index) => {
        parts.push(
          <Box key={prod.name + index} sx={{ marginBottom: '8px' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {prod.name}
            </Typography>
            <Typography variant="body2">
              {prod.description}
            </Typography>
            <Typography variant="body2">
              {prod["caratteristiche tecniche"]}
            </Typography>
            {/* Crea il link cliccabile con il link numerato */}
            <Typography variant="body2">
              <a href={prod["link puro"]} target="_blank" rel="noopener noreferrer">
                [Link {index + 1}]
              </a>
            </Typography>
            {/* Eventuale pulsante per i media */}
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleViewMedia(prod)}
              sx={{ marginTop: '4px' }}
            >
              Visualizza Media
            </Button>
          </Box>
        );
      });
    }
    
    // Visualizza il prompt se presente
    if (message.prompt) {
      parts.push(
        <Typography key="prompt" sx={{ fontStyle: 'italic', marginTop: '8px' }}>
          {message.prompt}
        </Typography>
      );
    }
    
    return parts;
  };
  
  const renderMessage = (message) => {
    return renderStructuredMessage(message);
  };
  
  return (
    <ThemeProvider theme={theme}>
      {!isProductDataLoaded ? (
        <Box sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
            width: '420px',
            height: '100%',
            maxHeight: '100%', // Massima altezza, tenendo conto del margine inferiore
            minHeight: '400px', // Altezza minima per evitare che diventi troppo piccolo
            '@media (max-width: 600px)': {
              width: '100%',
              height: '100vh', // Altezza intera sui dispositivi mobili
              right: 0,
              bottom: 0,
            },
          }}
        >
          <ProductMediaSidebar
            isOpen={isSidebarOpen}
            toggleSidebar={toggleSidebar}
            productMedia={productMedia}
            productData={productData}
          />
          <Paper
            elevation={3}
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 4,
              zIndex: 1000,
              overflow: 'hidden',
              '@media (max-width: 600px)': {
                borderRadius: 0,
              },
            }}
          >
            {/* Barra superiore */}
            <Box
              sx={{
                height: 100,
                px: 2,
                bgcolor: 'white',
                color: 'primary.contrastText',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                '@media (max-width: 600px)': {
                  height: 70, // Riduce l'altezza sui dispositivi mobili
                },
              }}
            >
              {/* Logo dell'azienda */}
              <img
                src="/media/images/logo.png"
                alt="Logo Azienda"
                style={{ height: '50%', objectFit: 'contain' }}
              />

              {/* Gruppo di icone */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {/* Icona Audio Parlato */}
                <IconButton
                  onClick={() => setIsTtsActive(!isTtsActive)}
                  size="small"
                  sx={{
                    color: isTtsActive ? '#cad50c' : 'black',
                  }}
                >
                  {isTtsActive ? (
                    <VoiceOnIcon style={{ width: 24, height: 24, fill: 'currentColor' }} />
                  ) : (
                    <VoiceOffIcon style={{ width: 24, height: 24, fill: 'currentColor' }} />
                  )}
                </IconButton>

                {/* Icona di aiuto */}
                <IconButton
                  onClick={handleHelpClick}
                  size="small"
                  sx={{
                    color: showHelp ? '#cad50c' : 'black',
                  }}
                >
                  <HelpOutlineIcon style={{ width: 24, height: 24, fill: 'currentColor' }} />
                </IconButton>

                {/* Icona per fissare un appuntamento */}
                <IconButton
                  onClick={() => setShowUserForm(!showUserForm)}
                  size="small"
                  sx={{
                    color: showUserForm ? '#cad50c' : 'black',
                  }}
                >
                  <HeadsetMicIcon style={{ width: 24, height: 24, fill: 'currentColor' }} />
                </IconButton>

                {/* Icona del microfono */}
                <IconButton
                  onClick={toggleVoiceRecognition}
                  size="small"
                  sx={{
                    color: isVoiceAssistantActive ? '#cad50c' : 'black',
                  }}
                >
                  {isVoiceAssistantActive ? (
                    <MicIcon style={{ width: 24, height: 24, fill: 'currentColor' }} />
                  ) : (
                    <MicOffIcon style={{ width: 24, height: 24, fill: 'currentColor' }} />
                  )}
                </IconButton>
              </Box>
            </Box>
            
            {/* Area dei messaggi */}
            <Box
              sx={{
                flexGrow: 1,
                overflowY: 'auto',
                p: 2,
                position: 'relative',
                '@media (max-width: 600px)': {
                  p: 1, // Riduce i padding sui dispositivi mobili
                },
              }}
            >
              {/* Mostra l'overlay solo se showHelp è true */}
              {showHelp && <HelpOverlay onClose={() => setShowHelp(false)} />}
              {messages.map((message, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                    mb: 1,
                  }}
                >
                  {/* Avatar del bot */}
                  {message.sender === 'bot' && (
                    <img
                      src="/media/images/avatar-chat.png"
                      alt="Avatar Bot"
                      style={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        marginRight: 8,
                      }}
                    />
                  )}
      
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1,
                      bgcolor: message.sender === 'user' ? 'primary.main' : 'grey.100',
                      color: message.sender === 'user' ? '#ffffff' : '#000000', // Colore del testo per l'utente e per il bot
                      maxWidth: '80%',
                      '@media (max-width: 600px)': {
                        maxWidth: '100%', // Larghezza completa sui dispositivi mobili
                      },
                    }}
                  >
                    {message.type === 'video' ? (
                      // Rendering del video
                      <video
                        ref={videoRef}
                        width="100%"
                        controls
                        onCanPlay={() => {
                          if (videoRef.current) {
                            videoRef.current.play().catch((error) => {
                              console.error('Errore nella riproduzione del video:', error);
                            });
                          }
                        }}
                        onEnded={() => {
                          startVoiceRecognition();
                        }}
                      >
                        <source src={message.content} type="video/mp4" />
                        Il tuo browser non supporta il tag video.
                      </video>
                    ) : message.options ? (
                      // Rendering del messaggio con opzioni (pulsanti)
                      <>
                        <ChatMessage message={message.text} />
                        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                          {message.options.map((option, idx) => (
                            <Button
                              key={idx}
                              variant="contained"
                              size="small"
                              onClick={() => handleOptionSelected(option.action)}
                            >
                              {option.text}
                            </Button>
                          ))}
                        </Box>
                      </>
                    ) : (
                      // Usa il nuovo renderMessage per gestire sia i messaggi plain che quelli strutturati
                      renderMessage(message)
                    )}
                  </Paper>
                </Box>
              ))}
      
              {/* Indicatore di caricamento */}
              {isLoading && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  {/* Avatar del bot */}
                  <img
                    src="/media/images/avatar-chat.png"
                    alt="Avatar Bot"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      marginRight: 8,
                    }}
                  />
      
                  {/* Animazione di caricamento */}
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1,
                      bgcolor: 'grey.100',
                      maxWidth: '80%',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <ThreeDots
                      height="40"
                      width="40"
                      radius="9"
                      color="#4fa94d"
                      ariaLabel="three-dots-loading"
                      wrapperStyle={{}}
                      wrapperClassName=""
                      visible={true}
                    />
                  </Paper>
                </Box>
              )}
      
              <div ref={endOfMessagesRef} />
      
              {/* Rendere il HelpOverlay se showHelp è true */}
              {showHelp && <HelpOverlay onClose={() => setShowHelp(false)} />}
            </Box>
      
            {/* Input utente o form */}
            {showUserForm ? (
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
                <UserForm onSubmit={handleUserFormSubmit} />
              </LocalizationProvider>
            ) : (
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  '@media (max-width: 600px)': {
                    p: 1, // Riduzione del padding per dispositivi mobili
                  },
                }}
              >
                {!isVoiceAssistantActive && (
                  <TextField
                    fullWidth
                    size="small"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleUserInput()}
                    placeholder="Scrivi un messaggio..."
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <IconButton size="small" color="primary" onClick={handleUserInput}>
                          <SendIcon style={{ width: 24, height: 24, fill: 'currentColor' }} />
                        </IconButton>
                      ),
                      style: { fontFamily: 'Montserrat, sans-serif' },
                    }}
                  />
                )}
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </ThemeProvider>
  );
};

export default ChatBot;
